import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  CreditCard, BookOpen, Users, TrendingUp, Search, 
  ExternalLink, Shield, Lock, Unlock, User, Mail, MessageSquare,
  ChevronRight, Check, DollarSign, Mic, Headphones, Volume2, Play,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import authService from '@/services/authService';
import fundingService from '@/services/fundingService';
import fileService from '@/services/fileService';
import AudioPlayerCard from '@/components/shared/AudioPlayerCard';
import ThemeToggle from '@/components/shared/ThemeToggle';

const goldButtonClass = 'bg-[#c98905] text-white hover:bg-[#a56f00]';
const goldOutlineClass = 'border-[#d8bd7a] bg-white text-[#9a6500] hover:bg-[#fff8eb]';

function StepNumber({ children }) {
  return (
    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#f5cf75] to-[#b77900] flex items-center justify-center text-white font-bold shadow-[0_8px_20px_rgba(185,119,0,0.22)]">
      {children}
    </div>
  );
}

export default function ParticipateV2() {
  const navigate = useNavigate();
  
  // State
  const [fundMode, setFundMode] = useState('CROWD'); // 'CROWD' or 'INDIVIDUAL'
  const [selectedBook, setSelectedBook] = useState(null);
  const [bookDetail, setBookDetail] = useState(null);
  const [selectedNarrator, setSelectedNarrator] = useState(null);
  const [narratorMode, setNarratorMode] = useState('AUTO'); // 'AUTO' or 'MANUAL'
  const [includeEncryption, setIncludeEncryption] = useState(false);
  const [amount, setAmount] = useState('');
  
  // UI State
  const [showBookModal, setShowBookModal] = useState(false);
  const [showNarratorModal, setShowNarratorModal] = useState(false);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [narrators, setNarrators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preferredGender, setPreferredGender] = useState('ANY');
  
  // Inquiry form
  const [inquiryEmail, setInquiryEmail] = useState('');
  const [inquiryMessage, setInquiryMessage] = useState('');
  
  // Audio preview state
  const [bookAssets, setBookAssets] = useState(null);
  const [showAudioModal, setShowAudioModal] = useState(false);

  // Load data on mount
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const summary = await fundingService.getParticipateSummary();
      setSearchResults(summary.featured_books || []);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        authService.clearTokens();
        navigate('/login');
      }
    }
  };

  // Search books
  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      loadInitialData();
      return;
    }
    try {
      const results = await fundingService.searchBooks(query);
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to search books');
    }
  };

  // Select book
  const handleSelectBook = async (book) => {
    setLoading(true);
    try {
      const detail = await fundingService.getBookDetail(book.id);
      setSelectedBook(book);
      setBookDetail(detail);
      setAmount(detail.remaining_amount.toString());
      setShowBookModal(true);
      
      // Load audio assets for preview
      try {
        const assets = await fileService.getBookAssets(book.id);
        setBookAssets(assets);
      } catch (e) {
        // Assets endpoint may not exist yet or no assets
        setBookAssets({ book_assets: [], narrator_demos: [], has_audio_preview: false });
      }
      
      // Load narrators if individual mode
      if (fundMode === 'INDIVIDUAL') {
        const narratorList = await fundingService.getNarrators(book.id);
        setNarrators(narratorList);
      }
    } catch (error) {
      console.error('Error loading book detail:', error);
      toast.error('Failed to load book details');
    } finally {
      setLoading(false);
    }
  };

  // Change fund mode
  const handleFundModeChange = (mode) => {
    setFundMode(mode);
    
    if (mode === 'CROWD') {
      setIncludeEncryption(true);
      setSelectedNarrator(null);
      setNarratorMode('AUTO');
    } else {
      setIncludeEncryption(false);
    }
  };

  // Open narrator selection
  const handleOpenNarratorSelection = async () => {
    if (!selectedBook) {
      toast.error('Please select a book first');
      return;
    }
    
    try {
      const narratorList = await fundingService.getNarrators(selectedBook.id);
      setNarrators(narratorList);
      setShowNarratorModal(true);
    } catch (error) {
      console.error('Error loading narrators:', error);
      toast.error('Failed to load narrators');
    }
  };

  // Select narrator
  const handleSelectNarrator = (narrator) => {
    setSelectedNarrator(narrator);
    setNarratorMode('MANUAL');
    setShowNarratorModal(false);
    toast.success(`Narrator ${narrator.display_name} selected`);
  };

  // Submit inquiry
  const handleSubmitInquiry = async () => {
    if (!inquiryEmail || !inquiryMessage) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await fundingService.createInquiry({
        book_id: selectedBook.id,
        email: inquiryEmail,
        message: inquiryMessage
      });
      toast.success('Inquiry submitted successfully');
      setShowInquiryModal(false);
      setInquiryEmail('');
      setInquiryMessage('');
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry');
    }
  };

  // Proceed to payment
  const handleProceedToPayment = async () => {
    if (!selectedBook) {
      toast.error('Please select a book');
      return;
    }

    if (!amount || parseFloat(amount) < 5) {
      toast.error('Minimum contribution is $5');
      return;
    }

    // Build payment URL with query params
    const params = new URLSearchParams({
      purpose: fundMode === 'CROWD' ? 'CROWD_CONTRIBUTION' : 'INDIVIDUAL_FUNDING',
      entity_type: 'BOOK',
      entity_id: selectedBook.id,
      amount: amount,
      currency: 'USD',
      allow_encryption: fundMode === 'INDIVIDUAL' ? 'true' : 'false',
      return_url: '/participate'
    });

    navigate(`/payment?${params.toString()}`);
  };

  const getStatusColor = (status) => {
    const colors = {
      'READY_FOR_RECORDING': 'bg-green-500',
      'RECORDING_IN_PROGRESS': 'bg-[#c98905]',
      'COMPLETED': 'bg-[#c98905]',
      'NEED_PUBLISHER_APPROVAL': 'bg-orange-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, ' ') || 'Unknown';
  };

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden bg-[#fffaf2] dark:bg-[#050504]">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_86%_8%,rgba(245,207,117,0.24),transparent_25%),linear-gradient(180deg,#ffffff_0%,#fffaf2_42%,#fff1d8_100%)] dark:bg-[radial-gradient(circle_at_82%_10%,rgba(224,165,27,0.30),transparent_22%),linear-gradient(180deg,#050504_0%,#090805_48%,#030302_100%)]" />
      <div className="pointer-events-none fixed inset-x-0 bottom-0 -z-10 h-[62vh] opacity-65 [background:repeating-radial-gradient(ellipse_at_center,rgba(224,165,27,0.18)_0_1px,transparent_1px_18px)] dark:opacity-35" />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-[#eadfcd] dark:border-[#2f260f] dark:bg-[#070705]/95">
        <div className="mx-auto max-w-[1800px] px-5 py-4 md:px-8">
          <div className="flex items-center justify-between gap-5">
            <div className="flex items-center gap-8 lg:gap-12">
              <button
                className="flex items-center text-left"
                onClick={() => navigate('/dashboard')}
              >
                <img
                  src="/images/golden-records-real-logo.png"
                  alt="Golden Records"
                  className="h-16 w-56 rounded-lg object-cover object-center"
                />
              </button>

              <nav className="hidden items-center gap-7 lg:flex">
                <button onClick={() => navigate('/dashboard')} className="text-sm font-medium text-slate-700 hover:text-[#b77900] dark:text-[#eee3cf] dark:hover:text-[#f2c95d]">
                  Dashboard
                </button>
                <button className="border-b-2 border-[#c98905] pb-2 text-sm font-medium text-[#b77900] dark:text-[#f2c95d]">
                  Participate
                </button>
                <button onClick={() => navigate('/publisher/workflow')} className="text-sm font-medium text-slate-700 hover:text-[#b77900] dark:text-[#eee3cf] dark:hover:text-[#f2c95d]">
                  Publisher
                </button>
                <button onClick={() => navigate('/narrator')} className="text-sm font-medium text-slate-700 hover:text-[#b77900] dark:text-[#eee3cf] dark:hover:text-[#f2c95d]">
                  Narrator
                </button>
                <button onClick={() => navigate('/work')} className="text-sm font-medium text-slate-700 hover:text-[#b77900] dark:text-[#eee3cf] dark:hover:text-[#f2c95d]">
                  Work
                </button>
              </nav>
            </div>

            <div className="flex items-center gap-3">
              <ThemeToggle />
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className={`${goldOutlineClass} dark:border-[#8a6a24] dark:bg-[#11100d] dark:text-[#f2c95d] dark:hover:bg-[#1b1710]`}
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 mx-auto max-w-[1700px] px-5 py-8 md:px-8">

        <div className="max-w-6xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold mb-4 text-slate-950 dark:text-[#f8efe0]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Preserve Literature <span className="text-[#b77900]">Together</span>
            </h2>
            <p className="text-xl text-slate-600 dark:text-[#d5c8ae]">
              Fund narration projects or become a narrator yourself
            </p>
          </div>

          {/* Funding Builder Card */}
          <Card className="bg-white/95 border border-[#eadfcd] rounded-2xl shadow-[0_22px_70px_rgba(82,55,12,0.10)] dark:border-[#4d3d16] dark:bg-[#0d0c09]/95 dark:shadow-[0_22px_70px_rgba(0,0,0,0.45)]" data-testid="funding-builder">
            <CardHeader>
              <CardTitle className="text-3xl" style={{ fontFamily: 'Playfair Display, serif' }}>Funding Builder</CardTitle>
              <CardDescription>Configure your contribution in 5 simple steps</CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* SECTION 1: Funding Mode Toggle */}
              <div className="space-y-4" data-testid="section-fund-mode">
                <div className="flex items-center gap-3">
                  <StepNumber>1</StepNumber>
                  <h3 className="text-xl font-semibold">Choose Funding Mode</h3>
                </div>
                
                <div className="flex justify-center">
                  <div className="relative inline-flex items-center p-1 bg-[#c98905] rounded-full">
                    <button
                      data-testid="fund-mode-crowd"
                      onClick={() => handleFundModeChange('CROWD')}
                      className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 ${
                        fundMode === 'CROWD'
                          ? 'bg-white text-[#9a6500] shadow-lg'
                          : 'bg-transparent text-white hover:bg-white/20'
                      }`}
                    >
                      Crowd Fund
                    </button>
                    <button
                      data-testid="fund-mode-individual"
                      onClick={() => handleFundModeChange('INDIVIDUAL')}
                      className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 ${
                        fundMode === 'INDIVIDUAL'
                          ? 'bg-white text-[#9a6500] shadow-lg'
                          : 'bg-transparent text-white hover:bg-white/20'
                      }`}
                    >
                      Individual Fund
                    </button>
                  </div>
                </div>

                <div className="text-center">
                  {fundMode === 'CROWD' ? (
                    <Badge className="bg-green-100 text-green-800">
                      <Shield className="w-4 h-4 mr-1" />
                      Encryption Included • Fixed Narrator
                    </Badge>
                  ) : (
                    <Badge className="bg-[#fff2d8] text-[#9a6500]">
                      <Unlock className="w-4 h-4 mr-1" />
                      Choose Your Narrator • Optional Encryption
                    </Badge>
                  )}
                </div>
              </div>

              <div className="border-t border-slate-200"></div>

              {/* SECTION 2: Select Book */}
              <div className="space-y-4" data-testid="section-select-book">
                <div className="flex items-center gap-3">
                  <StepNumber>2</StepNumber>
                  <h3 className="text-xl font-semibold">Select Book</h3>
                </div>

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <Input
                    data-testid="book-search-input"
                    placeholder="Search for books to fund..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-10 border-[#eadfcd] bg-white focus-visible:ring-[#c98905]"
                  />
                </div>

                {selectedBook && (
                  <div className="p-4 bg-[#fff8eb] rounded-xl border border-[#eadfcd]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <BookOpen className="w-6 h-6 text-[#c98905]" />
                        <div>
                          <p className="font-semibold text-slate-900">{selectedBook.title}</p>
                          <p className="text-sm text-slate-600">{selectedBook.country} • {selectedBook.language}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Audio Preview Icon */}
                        <button
                          data-testid="audio-preview-btn"
                          onClick={() => bookAssets?.has_audio_preview ? setShowAudioModal(true) : null}
                          className={`p-2 rounded-xl transition-all ${
                            bookAssets?.has_audio_preview
                              ? 'bg-[#c98905] text-white cursor-pointer hover:opacity-90 shadow-lg'
                              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                          }`}
                          title={bookAssets?.has_audio_preview ? 'Listen to audio preview' : 'No audio preview available'}
                        >
                          <Headphones className="w-5 h-5" />
                        </button>
                        <Check className="w-6 h-6 text-green-600" />
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                  {searchResults.map((book) => (
                    <Card
                      key={book.id}
                      data-testid={`book-card-${book.id}`}
                      className="cursor-pointer border-[#eadfcd] hover:border-[#d8bd7a] transition-all hover:shadow-lg"
                      onClick={() => handleSelectBook(book)}
                    >
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base">{book.title}</CardTitle>
                        <CardDescription className="text-xs">
                          {book.country} • {book.language}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold">${book.target_amount}</span>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="border-t border-slate-200"></div>

              {/* SECTION 3: Narrator Selection */}
              <div className="space-y-4" data-testid="section-narrator">
                <div className="flex items-center gap-3">
                  <StepNumber>3</StepNumber>
                  <h3 className="text-xl font-semibold">Narrator Selection</h3>
                </div>

                {fundMode === 'CROWD' ? (
                  <div className="p-6 bg-slate-100 rounded-xl text-center">
                    <Lock className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                    <p className="text-slate-600">Narrator is fixed for crowdfunding projects</p>
                    {bookDetail?.crowd_assigned_narrator && (
                      <Badge className="mt-2 bg-[#fff2d8] text-[#9a6500]">
                        Assigned: {bookDetail.crowd_assigned_narrator.display_name}
                      </Badge>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex gap-4">
                      <Button
                        data-testid="narrator-auto-button"
                        onClick={() => {
                          setNarratorMode('AUTO');
                          setSelectedNarrator(null);
                        }}
                        variant={narratorMode === 'AUTO' ? 'default' : 'outline'}
                        className={narratorMode === 'AUTO' ? goldButtonClass : goldOutlineClass}
                      >
                        Auto-select Best Narrator
                      </Button>
                      <Button
                        data-testid="narrator-manual-button"
                        onClick={handleOpenNarratorSelection}
                        variant={narratorMode === 'MANUAL' ? 'default' : 'outline'}
                        className={narratorMode === 'MANUAL' ? goldButtonClass : goldOutlineClass}
                        disabled={!selectedBook}
                      >
                        Choose from List
                      </Button>
                    </div>

                    {selectedNarrator && (
                      <div className="p-4 bg-[#fff8eb] rounded-xl border border-[#eadfcd]">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#f5cf75] to-[#9b6700] flex items-center justify-center text-white">
                            <User className="w-6 h-6" />
                          </div>
                          <div>
                            <p className="font-semibold">{selectedNarrator.display_name}</p>
                            <p className="text-sm text-slate-600">
                              {selectedNarrator.languages.join(', ')} • {selectedNarrator.country}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Preferred Gender (Optional)</Label>
                      <div className="flex gap-2">
                        {['MALE', 'FEMALE', 'ANY'].map((gender) => (
                          <Button
                            key={gender}
                            data-testid={`gender-${gender.toLowerCase()}`}
                            onClick={() => setPreferredGender(gender)}
                            variant={preferredGender === gender ? 'default' : 'outline'}
                            className={preferredGender === gender ? goldButtonClass : goldOutlineClass}
                            size="sm"
                          >
                            {gender}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t border-slate-200"></div>

              {/* SECTION 4: Progress Tracker */}
              {bookDetail && (
                <>
                  <div className="space-y-4" data-testid="section-progress">
                    <div className="flex items-center gap-3">
                      <StepNumber>4</StepNumber>
                      <h3 className="text-xl font-semibold">Progress Tracker</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {bookDetail.progress && (
                        <>
                          <ProgressItem label="Recording" value={bookDetail.progress.recording_pct} />
                          <ProgressItem label="Editing" value={bookDetail.progress.editing_pct} />
                          <ProgressItem label="Verification" value={bookDetail.progress.verification_pct} />
                          <ProgressItem label="QC" value={bookDetail.progress.qc_pct} />
                          <ProgressItem label="Encryption" value={bookDetail.progress.encryption_pct} />
                          <div className="flex items-center gap-3">
                            <div className="flex-1">
                              <div className="flex justify-between mb-2">
                                <span className="text-sm font-medium">Launched</span>
                              </div>
                              <Badge className={bookDetail.launched ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                                {bookDetail.launched ? 'Yes' : 'No'}
                              </Badge>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-slate-200"></div>
                </>
              )}

              {/* SECTION 5: Payment */}
              <div className="space-y-4" data-testid="section-payment">
                <div className="flex items-center gap-3">
                  <StepNumber>5</StepNumber>
                  <h3 className="text-xl font-semibold">Payment</h3>
                </div>

                {selectedBook && bookDetail ? (
                  <div className="space-y-4">
                    <div className="p-6 bg-[#fff8eb] rounded-xl border border-[#eadfcd]">
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-slate-600">Target Amount</p>
                          <p className="text-2xl font-bold text-slate-900">${bookDetail.target_amount}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Contributed</p>
                          <p className="text-2xl font-bold text-[#b77900]">{bookDetail.contributed_pct.toFixed(1)}%</p>
                        </div>
                      </div>
                      <Progress value={bookDetail.contributed_pct} className="h-3 bg-slate-200">
                        <div className="h-full bg-gradient-to-r from-[#b77900] to-[#f5cf75] transition-all"></div>
                      </Progress>
                      <p className="text-sm text-slate-600 mt-2">
                        ${bookDetail.contributed_amount.toFixed(2)} of ${bookDetail.target_amount} raised
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Your Contribution Amount</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                        <Input
                          data-testid="amount-input"
                          type="number"
                          placeholder="Enter amount"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          className="pl-10 border-[#eadfcd] bg-white focus-visible:ring-[#c98905]"
                          min="5"
                        />
                      </div>
                      <p className="text-xs text-slate-500">Minimum contribution: $5</p>
                    </div>

                    {fundMode === 'INDIVIDUAL' && (
                      <div className="flex items-center space-x-2 p-4 bg-amber-50 rounded-lg">
                        <Checkbox
                          data-testid="encryption-checkbox"
                          id="encryption"
                          checked={includeEncryption}
                          onCheckedChange={setIncludeEncryption}
                        />
                        <label htmlFor="encryption" className="text-sm font-medium cursor-pointer">
                          Encrypt this book for future generations (+${bookDetail.encryption_addon_price_for_individual})
                        </label>
                      </div>
                    )}

                    <Button
                      data-testid="proceed-payment-button"
                      onClick={handleProceedToPayment}
                      disabled={loading || !amount || parseFloat(amount) < 5}
                      className="w-full bg-[#c98905] hover:bg-[#a56f00] text-white py-6 text-lg rounded-xl shadow-lg"
                    >
                      <CreditCard className="w-5 h-5 mr-2" />
                      Proceed to Payment
                    </Button>
                  </div>
                ) : (
                  <div className="text-center p-8 bg-slate-100 rounded-xl">
                    <p className="text-slate-600">Please select a book to continue</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Book Detail Modal */}
      <Dialog open={showBookModal} onOpenChange={setShowBookModal}>
        <DialogContent className="max-w-3xl bg-white" data-testid="book-detail-modal">
          {bookDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {bookDetail.title}
                </DialogTitle>
                <DialogDescription>{bookDetail.description_short}</DialogDescription>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Book Info Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-600">Author</p>
                    <p className="font-semibold">{bookDetail.author || 'Unknown'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Genre</p>
                    <p className="font-semibold">{bookDetail.genre || 'General'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Country</p>
                    <p className="font-semibold">{bookDetail.country}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Language</p>
                    <p className="font-semibold">{bookDetail.language}</p>
                  </div>
                </div>

                {/* Recording Status */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Recording Status</p>
                    <p className="font-semibold">{formatStatus(bookDetail.recording_status)}</p>
                  </div>
                  <div className={`w-3 h-3 rounded-full ${getStatusColor(bookDetail.recording_status)}`}></div>
                </div>

                {/* Contribution Progress Circle */}
                <div className="flex items-center justify-center py-6">
                  <div className="relative w-48 h-48">
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      <circle
                        cx="50"
                        cy="50"
                        r="45"
                        fill="none"
                        stroke="#e5e7eb"
                        strokeWidth="8"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="45"
                        fill="none"
                        stroke="url(#gradient)"
                        strokeWidth="8"
                        strokeDasharray={`${bookDetail.contributed_pct * 2.83} 283`}
                        strokeLinecap="round"
                        transform="rotate(-90 50 50)"
                      />
                      <defs>
                        <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#b77900" />
                          <stop offset="100%" stopColor="#f5cf75" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <p className="text-3xl font-bold text-[#b77900]">
                        {bookDetail.contributed_pct.toFixed(1)}%
                      </p>
                      <p className="text-xs text-slate-600">Funded</p>
                    </div>
                  </div>
                </div>

                {/* Price Info */}
                <div className="flex items-center justify-between p-4 bg-[#fff8eb] border border-[#eadfcd] rounded-lg">
                  <div>
                    <p className="text-sm text-slate-600">Target Amount</p>
                    <p className="text-2xl font-bold">${bookDetail.target_amount}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-600">Remaining</p>
                    <p className="text-2xl font-bold text-orange-600">${bookDetail.remaining_amount.toFixed(2)}</p>
                  </div>
                </div>

                {/* Inquiry Button */}
                {bookDetail.recording_status === 'NEED_PUBLISHER_APPROVAL' && (
                  <Button
                    data-testid="inquiry-button"
                    onClick={() => setShowInquiryModal(true)}
                    variant="outline"
                    className="w-full border-2 border-orange-300 hover:bg-orange-50"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Request Approval / Copyright Inquiry
                  </Button>
                )}

                {bookDetail.listen_url && (
                  <a href={bookDetail.listen_url} target="_blank" rel="noopener noreferrer">
                    <Button className={`w-full ${goldButtonClass}`}>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Listen on Lysten AI
                    </Button>
                  </a>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Narrator Selection Modal */}
      <Dialog open={showNarratorModal} onOpenChange={setShowNarratorModal}>
        <DialogContent className="max-w-2xl bg-white" data-testid="narrator-modal">
          <DialogHeader>
            <DialogTitle>Select Narrator</DialogTitle>
            <DialogDescription>Choose a narrator for your individual funding</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-96 overflow-y-auto">
            {narrators.map((narrator) => (
              <Card
                key={narrator.id}
                data-testid={`narrator-card-${narrator.id}`}
                className="cursor-pointer border-[#eadfcd] hover:border-[#d8bd7a] transition-all"
                onClick={() => handleSelectNarrator(narrator)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#f5cf75] to-[#9b6700] flex items-center justify-center text-white text-2xl font-bold">
                      {narrator.display_name[0]}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-lg">{narrator.display_name}</p>
                      <p className="text-sm text-slate-600">{narrator.languages.join(', ')}</p>
                      <p className="text-xs text-slate-500">{narrator.country}</p>
                      {narrator.gender && (
                        <Badge className="mt-1">{narrator.gender}</Badge>
                      )}
                    </div>
                    {selectedBook && narrator.languages.includes(selectedBook.language) && (
                      <Badge className="bg-green-100 text-green-800">Best Match</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Inquiry Modal */}
      <Dialog open={showInquiryModal} onOpenChange={setShowInquiryModal}>
        <DialogContent className="bg-white" data-testid="inquiry-modal">
          <DialogHeader>
            <DialogTitle>Request Approval</DialogTitle>
            <DialogDescription>
              Request approval to make this title available for recording/funding
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="inquiry-email">Email</Label>
              <Input
                id="inquiry-email"
                data-testid="inquiry-email"
                type="email"
                placeholder="your@email.com"
                value={inquiryEmail}
                onChange={(e) => setInquiryEmail(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="inquiry-message">Message</Label>
              <textarea
                id="inquiry-message"
                data-testid="inquiry-message"
                className="w-full min-h-[100px] px-3 py-2 border border-slate-300 rounded-md"
                placeholder="Explain your request..."
                value={inquiryMessage}
                onChange={(e) => setInquiryMessage(e.target.value)}
              />
            </div>

            <Button
              data-testid="inquiry-submit"
              onClick={handleSubmitInquiry}
              className={`w-full ${goldButtonClass}`}
            >
              Submit Inquiry
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audio Preview Modal */}
      <Dialog open={showAudioModal} onOpenChange={setShowAudioModal}>
        <DialogContent className="max-w-lg bg-white" data-testid="audio-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#f5cf75] to-[#9b6700] flex items-center justify-center text-white">
                <Volume2 className="w-5 h-5" />
              </div>
              <span>Audio Preview</span>
            </DialogTitle>
            <DialogDescription>
              {selectedBook ? `Preview audio for "${selectedBook.title}"` : 'Listen to narrator demo audio'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[400px] overflow-y-auto">
            {bookAssets?.narrator_demos && bookAssets.narrator_demos.length > 0 ? (
              bookAssets.narrator_demos.map((demo, index) => (
                <AudioPlayerCard
                  key={demo.demo?.id || index}
                  src={fileService.getPublicFileUrl(demo.file_asset?.id)}
                  title={demo.demo?.title || `Demo ${index + 1}`}
                  subtitle={demo.demo?.description || 'Narrator demo audio'}
                  isPublic={true}
                />
              ))
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Headphones className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No audio previews available for this book yet</p>
                <p className="text-sm mt-1">Check back later or contact the publisher</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Progress Item Component
function ProgressItem({ label, value }) {
  return (
    <div className="space-y-2">
      <div className="flex justify-between">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <span className="text-sm font-bold text-slate-900">{value}%</span>
      </div>
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#b77900] to-[#f5cf75] transition-all duration-300"
          style={{ width: `${value}%` }}
        ></div>
      </div>
    </div>
  );
}
